import React, { useState, useEffect, useRef } from "react";
import {
  PrintProduct,
  CartUpdateItem
} from "../../../CheckoutPage/components/types";
import {
  updateCartItems,
  fetchPrintProducts,
  fetchCart
} from "../../../CheckoutPage/components/api";
import "./LightBoxPrintProducts.css";
import { formatPrice } from "../../../CheckoutPage/components/utils";
import PrintLightboxHeader from "./PrintLightboxHeader";

interface LightBoxPrintProductsProps {
  photo: any;
  event: any;
  cartId: number | null;
  primaryColor: string;
  updateCartFromApiResponse?: (apiResponse: any) => void;
  onCartStatusChange?: (hasCartItems: boolean) => void;
  canShowEmailPopup?: () => boolean;
  showSaveCartModal?: (photo: any, cartItem: any) => void;
  cart?: any;
  isEmbed?: boolean;
}

const LightBoxPrintProducts: React.FC<LightBoxPrintProductsProps> = ({
  photo,
  event,
  cartId,
  primaryColor,
  updateCartFromApiResponse,
  onCartStatusChange,
  canShowEmailPopup,
  showSaveCartModal,
  cart,
  isEmbed
}) => {
  const [printProducts, setPrintProducts] = useState<PrintProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [digitalSelected, setDigitalSelected] = useState(false);
  const [productQuantities, setProductQuantities] = useState<{
    [key: number]: number;
  }>({});
  const [existingSales, setExistingSales] = useState<any[]>([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [key: number]: boolean;
  }>({});
  const [flashMessages, setFlashMessages] = useState<{
    [key: number]: string;
  }>({});
  const flashTimersRef = useRef<number[]>([]);

  useEffect(() => {
    setDigitalSelected(false);
    setProductQuantities({});
    setExistingSales([]);
    setExpandedDescriptions({});
    setFlashMessages({});
    fetchPrintProductsData();
    initializeFromCart();
  }, [event.id, photo.id, cartId]);

  const fetchPrintProductsData = async () => {
    try {
      setLoading(true);
      const products = await fetchPrintProducts(event.id);
      setPrintProducts(products);
    } catch (error) {
      console.error("Error fetching print products:", error);
    } finally {
      setLoading(false);
    }
  };

  // Syncs local component state with existing cart data on mount/cart changes
  const initializeFromCart = async () => {
    try {
      if (!cartId) {
        if (onCartStatusChange) {
          onCartStatusChange(false);
        }
        return;
      }

      const cartData =
        cart?.sales && cart.id === cartId ? cart : await fetchCart(cartId);
      if (cartData && cartData.sales) {
        const hasDigitalPhoto = cartData.sales.some(
          (sale: any) =>
            sale.productType === "digital_photo_download" &&
            sale.photoId === photo.id
        );

        const photoSales = cartData.sales.filter(
          (sale: any) => sale.photoId === photo.id
        );
        setExistingSales(photoSales);

        const quantities: { [key: number]: number } = {};
        photoSales.forEach((sale: any) => {
          if (sale.productType === "print_product" && sale.printProductId) {
            quantities[sale.printProductId] =
              (quantities[sale.printProductId] || 0) + sale.quantity;
          }
        });

        setProductQuantities(quantities);
        setDigitalSelected(hasDigitalPhoto);

        // Notify parent component about initial cart status
        if (onCartStatusChange) {
          const hasCartItems =
            (!event.digitalDownloadDisabled && hasDigitalPhoto) ||
            Object.values(quantities).some(qty => qty > 0);
          onCartStatusChange(hasCartItems);
        }
      } else {
        if (onCartStatusChange) {
          onCartStatusChange(false);
        }
      }
    } catch (error) {
      console.error("Error initializing from cart:", error);
      if (onCartStatusChange) {
        onCartStatusChange(false);
      }
    }
  };

  const toggleDescription = (productId: number) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const isDescriptionLong = (description: string) => {
    return description && description.length > 100;
  };

  const showFlashMessage = (productId: number, message: string) => {
    setFlashMessages(prev => ({ ...prev, [productId]: message }));
    const t = window.setTimeout(() => {
      setFlashMessages(prev => ({ ...prev, [productId]: "" }));
    }, 2000);
    flashTimersRef.current.push(t);
  };

  useEffect(() => {
    return () => {
      flashTimersRef.current.forEach(clearTimeout);
      flashTimersRef.current = [];
    };
  }, []);

  // Watch for cart updates and sync local state
  useEffect(() => {
    if (!cart?.sales || !photo?.id) return;

    const photoSales = cart.sales.filter(
      (sale: any) => sale.photoId === photo.id
    );

    setExistingSales(photoSales);

    // Update digital selection
    const hasDigitalPhoto = photoSales.some(
      (sale: any) => sale.productType === "digital_photo_download"
    );
    setDigitalSelected(hasDigitalPhoto);

    // Update product quantities
    const quantities: { [key: number]: number } = {};
    photoSales.forEach((sale: any) => {
      if (sale.productType === "print_product" && sale.printProductId) {
        quantities[sale.printProductId] =
          (quantities[sale.printProductId] || 0) + sale.quantity;
      }
    });
    setProductQuantities(quantities);

    // Update cart status for parent component
    if (onCartStatusChange) {
      const hasCartItems =
        (!event.digitalDownloadDisabled && hasDigitalPhoto) ||
        Object.values(quantities).some(qty => qty > 0);
      onCartStatusChange(hasCartItems);
    }
  }, [cart?.sales, photo.id]);

  const isCurrencyMismatch = () => {
    if (
      cart &&
      event.currency !== cart.currency &&
      (cart.photos?.length || 0) > 0
    ) {
      //@ts-ignore
      toastr.error(
        `The cart can only contain photos in one currency, currently ${event.currency}. Clear and try adding again.`
      );
      return true;
    }
    return false;
  };

  const isDifferentEventPhoto = () => {
    if (
      cart &&
      cart.eventIds &&
      cart.eventIds.length >= 1 &&
      !cart.eventIds.includes(event.id) &&
      (cart.photos?.length || 0) > 0
    ) {
      //@ts-ignore
      toastr.error(
        `Your cart has photos from a different event. Please clear your cart before adding photos from this event.`
      );
      return true;
    }
    return false;
  };

  const handleDigitalToggle = async () => {
    const newDigitalSelected = !digitalSelected;

    if (newDigitalSelected && isCurrencyMismatch()) {
      return;
    }
    if (newDigitalSelected && isDifferentEventPhoto()) {
      return;
    }

    if (
      newDigitalSelected &&
      canShowEmailPopup &&
      canShowEmailPopup() &&
      showSaveCartModal
    ) {
      showSaveCartModal(photo, {
        type: "digital_photo_download",
        photo_id: photo.id,
        quantity: 1
      });
      return;
    }
    try {
      const prevSelected = digitalSelected;
      setDigitalSelected(newDigitalSelected);

      const ok = await updateCartSurgically("digital", newDigitalSelected);
      if (!ok) {
        setDigitalSelected(prevSelected);
      }
    } catch (error) {
      console.error("Error in handleDigitalToggle:", error);
    }
  };

  const updateProductQuantity = async (productId: number, change: number) => {
    const currentQty = productQuantities[productId] || 0;
    const newQty = Math.max(0, currentQty + change);
    const isAddingToCart = currentQty === 0 && newQty > 0;

    if (isAddingToCart && isCurrencyMismatch()) {
      return;
    }
    if (isAddingToCart && isDifferentEventPhoto()) {
      return;
    }

    if (
      isAddingToCart &&
      canShowEmailPopup &&
      canShowEmailPopup() &&
      showSaveCartModal
    ) {
      const product = printProducts.find(p => p.id === productId);
      const cartItem = {
        type: "print_product",
        photo_id: photo.id,
        print_product_id: productId,
        event_print_product_id: product?.eventPrintProductId,
        quantity: newQty
      };
      showSaveCartModal(photo, cartItem);
      return;
    }

    try {
      const prevQuantities = productQuantities;

      const nextQuantities = { ...productQuantities };
      if (newQty === 0) delete nextQuantities[productId];
      else nextQuantities[productId] = newQty;
      setProductQuantities(nextQuantities);

      const ok = await updateCartSurgically("product", newQty, productId);
      if (!ok) {
        // Revert UI on failure
        setProductQuantities(prevQuantities);
        return;
      }

      if (newQty > 0) {
        showFlashMessage(
          productId,
          isAddingToCart ? "Added to cart" : "Updated cart"
        );
      }
    } catch (error) {
      console.error("Error in updateProductQuantity:", error);
    }
  };

  // Precisely updates specific cart items without affecting other items
  const updateCartSurgically = async (
    itemKey: string,
    newValue: boolean | number,
    productId?: number
  ) => {
    try {
      const updates: CartUpdateItem[] = [];

      if (itemKey === "digital") {
        // Use latest cart data to check for existing sales
        const latestSales = existingSales || cart?.sales;
        // Find existing digital sale with multiple property name formats
        let existingDigitalSale = latestSales.find(
          sale =>
            (sale.productType === "digital_photo_download" ||
              sale.product_type === "digital_photo_download") &&
            (sale.photoId === photo.id || sale.photo_id === photo.id)
        );

        if (newValue && !existingDigitalSale) {
          // Add new digital photo
          updates.push({
            product_type: "digital_photo_download",
            photo_id: photo.id,
            quantity: 1
          });
        } else if (!newValue && existingDigitalSale) {
          // Remove existing digital photo
          updates.push({ id: existingDigitalSale.id, _destroy: true });
        } else if (newValue && existingDigitalSale) {
          // Digital photo already exists - no need to do anything, it's already in cart
          // This handles the case where user clicks "Add to Cart" when it's already added
        }
      } else if (productId !== undefined) {
        // Use latest cart data to check for existing sales, prioritizing local state
        const latestSales =
          existingSales.length > 0 ? existingSales : cart?.sales || [];
        const quantity = newValue as number;
        const product = printProducts.find(p => p.id === productId);
        const eventPrintProductId = product?.eventPrintProductId;

        // Find existing sale for this specific product
        let existingSale = latestSales.find(
          sale =>
            (sale.productType === "print_product" ||
              sale.product_type === "print_product") &&
            (sale.photoId === photo.id || sale.photo_id === photo.id) &&
            (sale.printProductId === productId ||
              sale.print_product_id === productId ||
              sale.eventPrintProductId === eventPrintProductId ||
              sale.event_print_product_id === eventPrintProductId)
        );

        // Fallback: if only one print sale exists and it matches this product, use it
        if (!existingSale) {
          const printSales = latestSales.filter(
            sale =>
              (sale.productType === "print_product" ||
                sale.product_type === "print_product") &&
              (sale.photoId === photo.id || sale.photo_id === photo.id)
          );

          if (printSales.length === 1) {
            const sale = printSales[0];
            const isMatchingProduct =
              sale.printProductId === productId ||
              sale.print_product_id === productId ||
              sale.eventPrintProductId === eventPrintProductId ||
              sale.event_print_product_id === eventPrintProductId;

            if (isMatchingProduct) {
              existingSale = sale;
            }
          }
        }

        if (existingSale && quantity === 0) {
          updates.push({ id: existingSale.id, _destroy: true });
        } else if (existingSale && quantity > 0) {
          updates.push({ id: existingSale.id, quantity: quantity });
        } else if (!existingSale && quantity > 0 && product) {
          if (eventPrintProductId == null) {
            console.warn(
              `Missing eventPrintProductId for product ${productId}`
            );
          }

          updates.push({
            product_type: "print_product",
            photo_id: photo.id,
            print_product_id: productId,
            event_print_product_id: eventPrintProductId,
            quantity: quantity
          });
        }
      }
      if (updates.length === 0) {
        return true;
      }
      const success = await updateCart(updates);
      return success;
    } catch (error) {
      console.error("Error updating cart surgically:", error);
      return false;
    }
  };

  // Sends cart update requests to the API and syncs response with local state
  const updateCart = async (
    items: CartUpdateItem[],
    skipSync: boolean = false
  ) => {
    try {
      // Allow cartId to be null - backend will handle cart creation
      const response = await updateCartItems(cartId, items);

      if (updateCartFromApiResponse && response) {
        updateCartFromApiResponse(response);

        // Force immediate sync of local state with the updated cart data
        if (response.cart && response.cart.sales) {
          const photoSales = response.cart.sales.filter(
            (sale: any) =>
              sale.photoId === photo.id || sale.photo_id === photo.id
          );
          setExistingSales(photoSales);

          // Also update cart status for parent component (cart badge)
          if (onCartStatusChange) {
            const hasDigitalPhoto = photoSales.some(
              (sale: any) => sale.productType === "digital_photo_download"
            );
            const hasPrintProducts = photoSales.some(
              (sale: any) =>
                sale.productType === "print_product" && sale.quantity > 0
            );
            const hasCartItems =
              (!event.digitalDownloadDisabled && hasDigitalPhoto) ||
              hasPrintProducts;
            onCartStatusChange(hasCartItems);
          }
        }
      }

      return response !== null;
    } catch (error) {
      console.error("Error updating cart:", error);
      return false;
    }
  };

  return (
    <div className="lightbox-print-products">
      {loading ? (
        <div className="loading-section">
          <i className="fa fa-spinner fa-spin"></i>
          <span>Loading products...</span>
        </div>
      ) : (
        <>
          {" "}
          <div className="hidden-xs">
            <PrintLightboxHeader
              cartId={cartId}
              primaryColor={primaryColor || "#259fa5"}
              hasCartItems={existingSales.some(
                sale =>
                  sale.productType === "print_product" ||
                  (sale.productType === "digital_photo_download" &&
                    !event.digitalDownloadDisabled)
              )}
              isEmbed={isEmbed}
            />
          </div>
          <div className="print-products-content">
            {!event.digitalDownloadDisabled && (
              <div className="lightbox-print-product-card digital-product">
                {/* Left Column: Thumbnail */}
                <div className="lightbox-print-product-thumbnail-column">
                  <div className="lightbox-print-product-thumbnail">
                    <img
                      src={photo.thumbnailUrl || photo.watermarkedImg}
                      alt="Digital version"
                      className="lightbox-print-product-image"
                    />
                  </div>
                </div>

                <div className="lightbox-print-product-info-column">
                  <h4 className="lightbox-print-product-title">
                    Digital version
                  </h4>
                  <p className="lightbox-print-desc lightbox-print-product-description">
                    High-resolution digital download
                  </p>
                </div>
                {/* Controls Column - Vertical Stack */}
                <div className="lightbox-print-product-controls-column">
                  <div className="lightbox-print-product-price">
                    {formatPrice(event.price, event.currency)} {event.currency}
                  </div>
                  <button
                    className={`lightbox-print-product-action-btn geo-btn geo-btn--small view-cart-btn ${
                      digitalSelected ? "selected" : ""
                    }`}
                    onClick={handleDigitalToggle}
                    style={{
                      backgroundColor: digitalSelected ? "transparent" : "#fff",
                      border: `1px solid ${primaryColor}`,
                      color: primaryColor
                    }}
                  >
                    {digitalSelected ? "Remove" : "Add to Cart"}
                  </button>
                </div>
              </div>
            )}

            {/* Print Products Section */}
            {printProducts.map(product => (
              <div
                key={product.id}
                className="lightbox-print-product-card print-product"
              >
                {/* Left Column: Thumbnail */}
                <div className="lightbox-print-product-thumbnail-column">
                  <div className="lightbox-print-product-thumbnail">
                    {product.previewImageThumbnailURL ? (
                      <img
                        src={product.previewImageThumbnailURL}
                        alt={product.title}
                        className="lightbox-print-product-image"
                      />
                    ) : (
                      <div className="placeholder-image">📷</div>
                    )}
                  </div>
                </div>

                {/* Product Info - Inline Layout */}
                <div className="lightbox-print-product-info-column">
                  <h4 className="lightbox-print-product-title">
                    {product.title}
                  </h4>
                  {product.description && (
                    <div>
                      <p
                        className={
                          expandedDescriptions[product.id]
                            ? "lightbox-print-desc"
                            : "lightbox-print-desc description-collapsed"
                        }
                      >
                        {product.description}
                      </p>
                      {isDescriptionLong(product.description) && (
                        <button
                          className="toggle-desc-see-more-btn"
                          onClick={() => toggleDescription(product.id)}
                          aria-expanded={!!expandedDescriptions[product.id]}
                          style={{
                            color: `${primaryColor}`
                          }}
                        >
                          {expandedDescriptions[product.id]
                            ? "See less"
                            : "See more"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Controls Column - Vertical Stack */}
                <div className="lightbox-print-product-controls-column">
                  <div className="lightbox-print-product-price">
                    {formatPrice(product.totalPrice, product.currency)}{" "}
                    {event.currency}
                  </div>
                  {productQuantities[product.id] > 0 ? (
                    <div className="quantity-controls">
                      <div className="quantity-labels-container">
                        <span className="quantity-label">Quantity</span>
                        {flashMessages[product.id] && (
                          <div
                            className="flash-message mobile-only"
                            style={{
                              color: primaryColor
                            }}
                          >
                            {flashMessages[product.id]}
                          </div>
                        )}
                      </div>
                      <div className="quantity-selector-container">
                        <div className="lightbox-print-quantity-selector">
                          <button
                            className="lightbox-print-quantity-btn minus"
                            onClick={() =>
                              updateProductQuantity(product.id, -1)
                            }
                            style={{
                              border: `1px solid ${primaryColor}`
                            }}
                          >
                            −
                          </button>
                          <span className="lightbox-print-quantity-display">
                            {productQuantities[product.id] || 0}
                          </span>
                          <button
                            className="lightbox-print-quantity-btn plus"
                            onClick={() => updateProductQuantity(product.id, 1)}
                            style={{
                              border: `1px solid ${primaryColor}`
                            }}
                          >
                            +
                          </button>
                        </div>
                        {flashMessages[product.id] && (
                          <div
                            className="flash-message desktop-only"
                            style={{
                              color: primaryColor
                            }}
                          >
                            {flashMessages[product.id]}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      className={`lightbox-print-product-action-btn geo-btn geo-btn--small view-cart-btn`}
                      onClick={() => updateProductQuantity(product.id, 1)}
                      style={{
                        backgroundColor: "#fff",
                        border: `1px solid ${primaryColor}`,
                        color: primaryColor
                      }}
                    >
                      Add to Cart
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LightBoxPrintProducts;
